import { baseApi } from "@/app/api/baseApi";

export interface AuthTokenResponse {
  access_token: string;
  token_type: string;
}

export interface SignInRequest {
  username_or_email: string;
  password: string;
}

export interface RequestPasswordResetRequest {
  email: string;
}

export interface ResendVerificationRequest {
  email: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

export interface AcceptInviteRequest {
  token: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export interface SSODiscoverRequest {
  email: string;
}

export interface SSODiscoverResponse {
  found: boolean;
  sso_config_id?: string | null;
  domain?: string | null;
}

export interface User {
  user_id: string;
  email: string;
  username: string;
  first_name?: string | null;
  last_name?: string | null;
  is_superuser?: boolean;
  is_admin?: boolean;
  website_ids?: string[] | null;
  onboarding_completed?: boolean;
  created_at: string;
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    signIn: builder.mutation<AuthTokenResponse, SignInRequest>({
      query: (body) => ({
        url: "/signin",
        method: "POST",
        body,
      }),
    }),
    refreshToken: builder.mutation<AuthTokenResponse, void>({
      query: () => ({
        url: "/refresh",
        method: "GET",
      }),
    }),
    signOut: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: "/signout",
        method: "POST",
      }),
    }),
    requestPasswordReset: builder.mutation<{ message: string }, RequestPasswordResetRequest>({
      query: (body) => ({
        url: "/request-password-reset",
        method: "POST",
        body,
      }),
    }),
    resendVerification: builder.mutation<{ message: string }, ResendVerificationRequest>({
      query: (body) => ({
        url: "/resend-verification",
        method: "POST",
        body,
      }),
    }),
    verifyEmail: builder.mutation<{ message: string }, VerifyEmailRequest>({
      query: (body) => ({
        url: "/verify-email",
        method: "POST",
        body,
      }),
    }),
    resetPassword: builder.mutation<{ message: string }, ResetPasswordRequest>({
      query: (body) => ({
        url: "/reset-password",
        method: "POST",
        body,
      }),
    }),
    acceptInvite: builder.mutation<
      { message: string; user_id: string; username: string },
      AcceptInviteRequest
    >({
      query: (body) => ({
        url: "/accept-invite",
        method: "POST",
        body,
      }),
    }),
    getMe: builder.query<User, void>({
      query: () => "/users/me",
      providesTags: ["User"],
    }),
    discoverSSO: builder.mutation<SSODiscoverResponse, SSODiscoverRequest>({
      query: (body) => ({
        url: "/saml/discover",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useSignInMutation,
  useRefreshTokenMutation,
  useSignOutMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  useRequestPasswordResetMutation,
  useResendVerificationMutation,
  useVerifyEmailMutation,
  useResetPasswordMutation,
  useAcceptInviteMutation,
  useDiscoverSSOMutation,
} = authApi;

/** Backend OAuth entrypoints (full-page redirect, not RTK Query).
 *
 * The optional `returnTo` is forwarded to the backend so it knows which
 * frontend origin (e.g. https://beta.zenyt.ai) to land the user back on
 * after the IdP roundtrip, instead of always using the backend's
 * FRONTEND_URL env var. The backend validates this against its allowlist
 * to prevent open-redirect.
 */
function buildAuthUrl(provider: "google" | "microsoft", returnTo?: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "/api/v2";
  const origin =
    returnTo ?? (typeof window !== "undefined" ? window.location.origin : undefined);
  const qs = origin ? `?return_to=${encodeURIComponent(origin)}` : "";
  return `${base}/${provider}${qs}`;
}

export function googleAuthUrl(returnTo?: string): string {
  return buildAuthUrl("google", returnTo);
}

export function microsoftAuthUrl(returnTo?: string): string {
  return buildAuthUrl("microsoft", returnTo);
}

/** Backend SAML SSO entrypoint (full-page redirect, not RTK Query).
 *
 * After `POST /saml/discover` resolves an `sso_config_id` for the user's email
 * domain, we hand the browser off to the IdP via the backend. `returnTo` is
 * validated against the backend allowlist (same as OAuth) so the user lands
 * back on the correct frontend origin after the SAML roundtrip.
 */
export function samlLoginUrl(configId: string, returnTo?: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "/api/v2";
  const origin =
    returnTo ?? (typeof window !== "undefined" ? window.location.origin : undefined);
  const qs = origin ? `?return_to=${encodeURIComponent(origin)}` : "";
  return `${base}/saml/login/${encodeURIComponent(configId)}${qs}`;
}
